import * as React from "react";
import s from "./fluxo.scss";

interface AnimatedBarProps {
	value: number;
	maxValue: number;
	label: string;
	delay?: number;
}

export function AnimatedBar({ value, maxValue, label, delay = 0 }: AnimatedBarProps) {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = React.useState(false);
	const [width, setWidth] = React.useState(0);

	React.useEffect(() => {
		const element = containerRef.current;
		if (!element) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 },
		);

		observer.observe(element);

		return () => observer.disconnect();
	}, []);

	React.useEffect(() => {
		if (!isVisible) return;

		const targetWidth = (value / maxValue) * 100;
		const startTime = performance.now();
		const duration = 1200;

		function animate(currentTime: number) {
			const elapsed = currentTime - startTime - delay;
			if (elapsed < 0) {
				requestAnimationFrame(animate);
				return;
			}

			const progress = Math.min(elapsed / duration, 1);

			const easeOutCubic = (t: number): number => {
				return 1 - Math.pow(1 - t, 3);
			};

			const easedProgress = easeOutCubic(progress);
			const currentWidth = targetWidth * easedProgress;

			setWidth(currentWidth);

			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				setWidth(targetWidth);
			}
		}

		requestAnimationFrame(animate);
	}, [isVisible, value, maxValue, delay]);

	const showText = width >= 15;

	return (
		<div ref={containerRef} className={s.chartBarContainer}>
			<div className={s.chartBar} style={{ width: `${width}%` }}>
				<span style={{ opacity: showText ? 1 : 0 }}>{label}</span>
			</div>
		</div>
	);
}
